// React core components
import React, { useState, useEffect } from "react";

// @mui Material components
import {
  Grid,
  Typography,
  Icon,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";

// Material Dashboard 2 PRO React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Services
import crudsService from "services/cruds-service";
import DataTable from "examples/Tables/DataTable";

// Custom components
import CouponModal from "./CouponModal";

const MODE = {
  ADD: 'add',
  EDIT: 'edit',
  CLONE: 'clone'
};


const CouponForm = () => {
  // State Variables

  // Coupons and courses data
  const [allCoupons, setAllCoupons] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [couponUsers, setCouponUsers] = useState([]);
  const [couponNewUsers, setCouponNewUsers] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  // Notification state
  const [notification, setNotification] = useState({
    value: false,
    color: "info",
    message: "",
  });

  // Modal & form input states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [couponName, setCouponName] = useState("");

  const currentDate = new Date();
  const nextWeek = new Date(currentDate); // Create a valid copy of the current date
  nextWeek.setDate(nextWeek.getDate() + 7); // Add 7 days
  const formattedDate = nextWeek.toLocaleDateString("en-CA");

  const [expiryDate, setExpiryDate] = useState(formattedDate); // Convert to ISO format


  const [disabled, setDisabled] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [editCouponCode, setEditCouponCode] = useState("");
  const [mode, setMode] = useState(MODE.ADD);  // Set default mode to 'add'

  // Deletion modal state
  const [couponToDelete, setCouponToDelete] = useState("");
  const [confirmDeletionDialog, setConfirmDeletionDialog] = useState(false);
  const [couponError, setCouponError] = useState("");

  const getRegistrationLink = (accessCode) => {
    const appUrl = (process.env.REACT_APP_URL || "").replace(/\/$/, "");
    return `${appUrl}/auth/register?accesscode=${encodeURIComponent(accessCode)}`;
  };

  // Effect Hooks

  // Fetch courses on component mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const courses = await crudsService.getCourses();
        let editedCourses = courses.data.map((course) => ({
          id: course.id,
          app_name: course.attributes.app_name,
        }));
        setAllCourses(editedCourses);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  // Fetch coupons on component mount
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const coupons = await crudsService.getCoupons();
        let editedCoupons = coupons.data.map((coupon) => ({
          id: coupon.id,
          code: coupon.code,
          expiryDate: coupon.expiryDate,
          disabled: coupon.disabled,
          courses: coupon.courses.map((course) => ({
            id: course._id,
            app_name: course.app_name,
          })),
        }));
        setAllCoupons(editedCoupons);
        setTableData(editedCoupons);
      } catch (error) {
        setNotification({
          value: true,
          color: "error",
          message: "Error fetching coupons",
        });
        console.error("Error fetching coupons:", error);
      }
    };

    const fetchUsers = async () => {
      try {
        const users = await crudsService.getUsers();
        let editedUsers = users.data.map((user) => ({
          id: user.id,
          applied_coupons: user?.applied_coupons || [],
          name: user?.name,
          email: user?.email,
        }));
        setAllUsers(editedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchCoupons();
    fetchUsers();
  }, []);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification.value === true) {
      const timer = setTimeout(() => {
        setNotification({ value: false, color: "info", message: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // DataTable Configuration
  const dataTableData = {
    columns: [
      {
        Header: "code",
        accessor: "code",
        width: "15%",
        Cell: (info) => (
          <MDTypography variant="text" color="dark">
            {/* if the expiry date has pased, show an icon with tooltip saying the expiry date for this coupon has passed */}
            {new Date(info.cell.row.original.expiryDate) < new Date() ? (
              <>
              <Tooltip title="Expiry date for the coupon has passed">
                <Icon color="error">error_outlined</Icon>{" "}
              </Tooltip>
              {info.cell.row.original.code}
              </>
            ) : (
              info.cell.row.original.code
            )}
          </MDTypography>
        ),
      },
      {
        Header: "Courses",
        accessor: "courses",
        width: "50%",
        style: { whiteSpace: "normal" },
        Cell: (info) => (
          <Grid width={"40vw"}>
            {info.cell.row.original.courses.map((course, index) => (
              <Chip key={index} label={course.app_name} sx={{ margin: "5px" }} />
            ))}
          </Grid>
        ),
      },
      {
        Header: "Actions",
        width: "20%",
        Cell: (info) => (
          <MDBox
            display="flex"
            alignItems="center"
            style={{ justifyContent: "center", columnGap: "12px", minWidth: "96px" }}
          >
            {/* Copy registration link */}
            <Tooltip title="Copy registration link">
              <Icon
                color="info"
                fontSize="medium"
                style={{ cursor: "pointer" }}
                onClick={() => handleCopyRegistrationLink(info.cell.row.original.code)}
              >
                link
              </Icon>
            </Tooltip>
            {/* Edit coupon */}
            <Tooltip title="Edit coupon">
              <Icon
                color="info"
                fontSize="medium"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setCouponName(info.cell.row.original.code);
                  setDisabled(info.cell.row.original.disabled);
                  setExpiryDate(info.cell.row.original.expiryDate);
                  setSelectedCourses(info.cell.row.original.courses);
                  setMode(MODE.EDIT);
                  setEditCouponCode(info.cell.row.original.id);
                  setCouponUsers(allUsers.filter(user => user.applied_coupons.includes(info.cell.row.original.id)))
                  setCouponNewUsers(allUsers.filter(user => user.applied_coupons.includes(info.cell.row.original.id)))
                  handleOpenAddModal();
                }}
              >
                edit
              </Icon>
            </Tooltip>
            {/* Clone coupon */}
            <Tooltip title="Clone coupon">
              <Icon
                color="info"
                fontSize="medium"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setCouponName(info.cell.row.original.code + "_COPY");
                  setDisabled(info.cell.row.original.disabled);
                  setExpiryDate(info.cell.row.original.expiryDate);
                  setSelectedCourses(info.cell.row.original.courses);
                  setMode(MODE.CLONE);
                  setEditCouponCode(info.cell.row.original.id);
                  handleOpenAddModal();
                }}
              >
                content_copy
              </Icon>
            </Tooltip>
            {/* Delete coupon */}
            {/* <Icon
              color={info.cell.row.original.code === "PUBLIC" ? "disabled" : "error"}
              fontSize="medium"
              style={{
                cursor: info.cell.row.original.code === "PUBLIC" ? "not-allowed" : "pointer",
                marginLeft: "10px",
              }}
              onClick={() => {
                if (info.cell.row.original.code !== "PUBLIC") {
                  setCouponToDelete(info.cell.row.original.id);
                  setConfirmDeletionDialog(true);
                }
              }}
            >
              delete
            </Icon> */}
          </MDBox>
        ),
      },
    ],
    rows: tableData,
  };

  // Event Handlers

  // Open Add/Edit Modal
  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  // Close Modal and reset inputs
  const handleCloseAddModal = () => {
    setCouponName("");
    setDisabled(false);
    setExpiryDate(nextWeek);
    setCouponError("");
    setSelectedCourses([]);
    setCouponNewUsers([]);
    setCouponUsers([]);
    setOpenAddModal(false);
  };

  // Submit a new coupon
  const handleCouponSubmit = async () => {
    const courseNamesToIds = selectedCourses.map((course) => course.id);
    try {
      const response = await crudsService.createCoupon({
        code: couponName,
        courses: courseNamesToIds,
        disabled: disabled,
        expiryDate: expiryDate, // Now expiryDate is a Date object
      });
      // Append new coupon data to current list
      setAllCoupons([...allCoupons, response.data]);
      setTableData([
        ...tableData,
        { code: couponName, courses: selectedCourses, id: response.data.id, disabled: response.data.disabled, expiryDate: response.data.expiryDate },
      ]);
      setNotification({
        value: true,
        color: "success",
        message: "Coupon created successfully",
      });
    } catch (error) {
      setNotification({
        value: true,
        color: "error",
        message: "Error creating coupon",
      });
      console.error("Error creating coupon:", error);
    }
    handleCloseAddModal();
  };

  // Update an existing coupon
  const handleEditCoupon = async () => {
    const courseNamesToIds = selectedCourses.map((course) => course.id);
    const removedUsers = couponUsers.filter(
      (initialUser) => !couponNewUsers.some((newUser) => newUser.id === initialUser.id)
    );
    try {
      await crudsService.updateCoupon({
        coupon_id: editCouponCode,
        code: couponName,
        courses: courseNamesToIds,
        disabled: disabled,
        expiryDate: expiryDate,
        newUsers: couponNewUsers,
        removedUsers: removedUsers,
      });
      // Update coupon in our state
      const updatedCoupons = allCoupons.map((coupon) => {
        if (coupon.id === editCouponCode) {
          return {
            id: editCouponCode,
            code: couponName,
            courses: selectedCourses,
            disabled: disabled,
            expiryDate: expiryDate,
          };
        }
        return coupon;
      });
      setAllCoupons(updatedCoupons);
      setTableData(updatedCoupons);
      handleCloseAddModal();
      setNotification({
        value: true,
        color: "success",
        message: "Coupon updated successfully",
      });
    } catch (error) {
      console.log("Error: ", error.message);
      setNotification({
        value: true,
        color: "error",
        message: "Error updating coupon",
      });
      console.error("Error updating coupon:", error);
    }
  };

  // Delete a coupon
  const handleDeleteCoupon = async (couponId) => {
    try {
      await crudsService.deleteCoupon(couponId);
      const updatedCoupons = allCoupons.filter(
        (coupon) => coupon.id !== couponId
      );
      setAllCoupons(updatedCoupons);
      setTableData(updatedCoupons);
      setNotification({
        value: true,
        color: "success",
        message: "Coupon deleted successfully",
      });
    } catch (error) {
      setNotification({
        value: true,
        color: "error",
        message: "Error deleting coupon",
      });
      console.error("Error deleting coupon:", error);
    }
  };

  // Clone a coupon
  const handleCouponClone = async () => {
    handleCouponSubmit();
  };

  const handleCopyRegistrationLink = async (accessCode) => {
    const registrationLink = getRegistrationLink(accessCode);

    try {
      await navigator.clipboard.writeText(registrationLink);
      setNotification({
        value: true,
        color: "success",
        message: "Registration link copied to clipboard",
      });
    } catch (error) {
      setNotification({
        value: true,
        color: "error",
        message: "Unable to copy registration link",
      });
      console.error("Error copying registration link:", error);
    }
  };

  return (
    <>
      <CouponModal
        open={openAddModal}
        handleClose={handleCloseAddModal}
        couponNewUsers={couponNewUsers}
        setCouponNewUsers={setCouponNewUsers}
        allUsers={allUsers}
        allCoupons={allCoupons}
        disabled={disabled}
        setDisabled={setDisabled}
        expiryDate={expiryDate}
        setExpiryDate={setExpiryDate}
        couponName={couponName}
        setCouponName={setCouponName}
        selectedCourses={selectedCourses}
        setSelectedCourses={setSelectedCourses}
        allCourses={allCourses}
        handleCouponSubmit={handleCouponSubmit}
        handleEditCoupon={handleEditCoupon}
        handleCouponClone={handleCouponClone}
        couponError={couponError}
        setCouponError={setCouponError}
        currentMode={mode}
        MODE={MODE}
      />
      <Dialog open={confirmDeletionDialog} onClose={() => {
        setCouponToDelete("");
        setConfirmDeletionDialog(false)
      }}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this coupon?</Typography>
        </DialogContent>
            {/* Copy registration link */}
            <Tooltip title="Copy registration link">
              <Icon
                color="info"
                fontSize="medium"
                style={{ cursor: "pointer" }}
                onClick={() => handleCopyRegistrationLink(info.cell.row.original.code)}
              >
                link
              </Icon>
            </Tooltip>
        <DialogActions>
          <MDButton onClick={() => {
            setCouponToDelete("");
            setConfirmDeletionDialog(false)
          }
          } color="secondary">
            Cancel
          </MDButton>
          <MDButton
            onClick={() => {
              handleDeleteCoupon(couponToDelete);
              setConfirmDeletionDialog(false);
            }}
            color="error"
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
      {
        notification.value === true && (
          <Snackbar open>
            <Alert
              variant="filled"
              sx={{ width: "100%" }}
              color={notification.color}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        )
      }
      {/* {handleOpenAddModal} */}
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              display={"flex"}
              justifyContent={"space-between"}
            >
              <Typography variant="h3" >
                Access Code
              </Typography>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                type="button"
                onClick={() => {
                  setMode(MODE.ADD);
                  setCouponName("");
                  setDisabled(false);
                  setExpiryDate(nextWeek);
                  setSelectedCourses([]);
                  setCouponError("");
                  handleOpenAddModal();
                }}
                style={{ verticalAlign: "middle" }}
              >
                <Icon style={{ verticalAlign: "middle", marginRight: "10px" }}>
                  add
                </Icon>{" "}
                Add New
              </MDButton>
            </Grid>
            <Grid item xs={12}>
              <DataTable
                table={dataTableData}
                entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20] }}
                canSearch={true}
              />
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    </>
  );
};

export default CouponForm;
